import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ChatService } from '../../services/chat.service';
import { GlobalService } from '../../services/global.service';

@Component({
  selector: 'app-messenger-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './messenger-panel.component.html',
  styles: [`
    .bg-blue-50 { background-color: #f0f7ff !important; }
    .text-blue-600 { color: #3b82f6 !important; }
    .bg-blue-100 { background-color: #dbeafe !important; }
  `]
})
export class MessengerPanelComponent implements OnChanges, OnDestroy {
  @Input() is_open = false;
  @Output() close = new EventEmitter<void>();

  view: 'list' | 'chat' = 'list';

  subjects: any[] = [];
  selected_subject: any = null;
  recipients: any[] = [];
  selected_recipient: any = null;

  // Active chat context — kept separate from dropdown bindings to avoid ng-select interference
  active_subject: any = null;
  active_recipient: any = null;

  conversations: any[] = [];
  grouped_conversations: { label: string; items: any[] }[] = [];
  messages: any[] = [];
  message_text = '';

  loading_subjects = false;
  loading_recipients = false;
  loading_messages = false;
  loading_conversations = false;
  sending = false;

  private polling_ref: any = null;

  constructor(private chat: ChatService, public gs: GlobalService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['is_open']?.currentValue === true) {
      this.init_panel();
    } else if (changes['is_open']?.currentValue === false) {
      this.stop_polling();
    }
  }

  async init_panel() {
    this.view = 'list';
    this.selected_subject = null;
    this.selected_recipient = null;
    this.active_subject = null;
    this.active_recipient = null;
    this.recipients = [];
    this.messages = [];
    await Promise.all([this.load_subjects(), this.load_conversations()]);
  }

  async load_subjects() {
    this.loading_subjects = true;
    try {
      const res = await this.chat.get_chat_subjects();
      this.subjects = res?.data || [];
    } catch {
      this.subjects = [];
    } finally {
      this.loading_subjects = false;
    }
  }

  async load_conversations() {
    this.loading_conversations = true;
    try {
      const res = await this.chat.get_conversations();
      this.conversations = res?.data || [];
      this.grouped_conversations = this.build_grouped_conversations();
    } catch {
      this.conversations = [];
      this.grouped_conversations = [];
    } finally {
      this.loading_conversations = false;
    }
  }

  async on_subject_change(subject: any) {
    this.selected_recipient = null;
    this.recipients = [];
    if (!subject) return;
    this.loading_recipients = true;
    try {
      const res = await this.chat.get_eligible_recipients(subject.id);
      this.recipients = res?.data || [];
    } catch {
      this.recipients = [];
    } finally {
      this.loading_recipients = false;
    }
  }

  open_chat_from_list(convo: any) {
    this.active_subject = convo.subject;
    this.active_recipient = convo.other_user;
    this.start_chat();
  }

  open_chat_from_dropdowns() {
    if (!this.selected_subject || !this.selected_recipient) return;
    this.active_subject = this.selected_subject;
    this.active_recipient = this.selected_recipient;
    this.start_chat();
  }

  async start_chat() {
    this.view = 'chat';
    this.messages = [];
    await this.load_messages();
    this.start_polling();
  }

  async load_messages() {
    if (!this.active_recipient?.id || !this.active_subject?.id) return;
    this.loading_messages = true;
    try {
      const res = await this.chat.get_message_list(
        this.active_recipient.id,
        this.active_subject.id
      );
      this.messages = res?.data || [];
      this.scroll_to_bottom();
    } catch {
      this.messages = [];
    } finally {
      this.loading_messages = false;
    }
  }

  start_polling() {
    this.stop_polling();
    this.polling_ref = setInterval(() => this.poll_messages(), 5000);
  }

  async poll_messages() {
    if (!this.active_recipient?.id || !this.active_subject?.id) return;
    try {
      const res = await this.chat.get_message_list(
        this.active_recipient.id,
        this.active_subject.id
      );
      const new_messages = res?.data || [];
      if (new_messages.length !== this.messages.length) {
        this.messages = new_messages;
        this.scroll_to_bottom();
      }
    } catch {}
  }

  stop_polling() {
    if (this.polling_ref) {
      clearInterval(this.polling_ref);
      this.polling_ref = null;
    }
  }

  strip_numbers(event: Event) {
    const el = event.target as HTMLTextAreaElement;
    const cleaned = el.value.replace(/[0-9]/g, '');
    if (cleaned !== el.value) {
      this.message_text = cleaned;
      el.value = cleaned;
    }
  }

  async send() {
    if (!this.message_text.trim() || this.sending) return;
    if (!this.active_recipient?.id || !this.active_subject?.id) return;

    this.sending = true;
    const text = this.message_text.trim();
    this.message_text = '';

    try {
      await this.chat.send_message(
        this.active_recipient.id,
        this.active_subject.id,
        text
      );
      await this.load_messages();
    } catch (err: any) {
      this.gs.toastr_shows_function(err?.error?.message || 'Failed to send message', 'Error', 'error');
      this.message_text = text;
    } finally {
      this.sending = false;
    }
  }

  on_enter(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  go_back() {
    this.stop_polling();
    this.view = 'list';
    this.load_conversations();
  }

  on_close() {
    this.stop_polling();
    this.close.emit();
  }

  scroll_to_bottom() {
    setTimeout(() => {
      const el = document.getElementById('chat-messages-container');
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }

  is_my_message(msg: any): boolean {
    return msg.sender_id === this.gs.user_data?.id;
  }

  get_initials(name: string): string {
    if (!name) return '?';
    return name
      .split(' ')
      .map((w: string) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  build_grouped_conversations(): { label: string; items: any[] }[] {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: { [key: string]: any[] } = {};

    for (const c of this.conversations) {
      const d = new Date(c.last_message_at);
      let label: string;

      if (this.is_same_day(d, today)) {
        label = 'Today';
      } else if (this.is_same_day(d, yesterday)) {
        label = 'Yesterday';
      } else {
        label = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
      }

      if (!groups[label]) groups[label] = [];
      groups[label].push(c);
    }

    return Object.entries(groups).map(([label, items]) => ({ label, items }));
  }

  track_by_group(_: number, group: any): string { return group.label; }
  track_by_convo(_: number, convo: any): string { return `${convo.other_user?.id}_${convo.subject?.id}`; }

  group_messages_by_date(): { label: string; items: any[] }[] {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: { [key: string]: any[] } = {};

    for (const m of this.messages) {
      const d = new Date(m.createdAt);
      let label: string;

      if (this.is_same_day(d, today)) {
        label = 'Today';
      } else if (this.is_same_day(d, yesterday)) {
        label = 'Yesterday';
      } else {
        label = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
      }

      if (!groups[label]) groups[label] = [];
      groups[label].push(m);
    }

    return Object.entries(groups).map(([label, items]) => ({ label, items }));
  }

  format_time(date_str: string): string {
    const d = new Date(date_str);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  }

  private is_same_day(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
  }

  ngOnDestroy() {
    this.stop_polling();
  }
}
